import * as React from 'react';
import withStyles from 'react-jss';
import { CssType, ThemeType } from '@theming/jssTypes';
import { Button, Modal, Carousel, Tag } from 'antd';
import { LeftCircleOutlined, RightCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import FixedHeaderTable from '@components/common/FixedHeaderTable/FixedHeaderTable';
import { SuccessIcon } from 'src/components/common/icons/opsDashboard/SuccessIcon';
import { ErrorIcon2 } from 'src/components/common/icons/opsDashboard/ErrorIcon2';

const styles = (theme: ThemeType): CssType => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
  },
  detailsCard: {
    margin: '0 0 20px 0',
    padding: '20px 24px',
    backgroundColor: '#ffffff',
    border: '1px solid #D9D9D9',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'stretch',
    gap: '24px',
    overflowX: 'auto',
    flexWrap: 'nowrap',
    width: '98%',
    marginLeft: '10px',
    boxSizing: 'border-box',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '200px',
    maxWidth: '350px',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    textAlign: 'left',
    gap: '8px',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#595959',
    fontWeight: 400,
    lineHeight: '1.5',
    textAlign: 'left',
    marginBottom: '4px',
  },
  detailValue: {
    fontSize: '14px',
    color: '#262626',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    textAlign: 'left',
  },
  statusTag: {
    fontWeight: 'bold',
    textTransform: 'capitalize',
    fontSize: '13px',
    padding: '2px 8px',
    borderRadius: '4px',
    border: 'none',
  },
  tableContainer: {
    flex: '1 1 0',
    overflow: 'auto',
    '& .ant-table': {
      boxShadow: 'none !important',
    },
    '& .ant-table-container': {
      boxShadow: 'none !important',
    },
    '& .ant-table-tbody > tr > td, .ant-table-thead > tr > th': {
      padding: 9,
      textAlign: 'center',
    },
    height: '100%',
  },
  imageButton: {
    margin: '2px',
    fontSize: '12px',
  },
  resultTag: {
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '12px',
    fontSize: '12px',
    padding: '4px 12px',
    minWidth: '70px',
    textAlign: 'center',
    display: 'inline-block',
  },
  correctTag: {
    backgroundColor: '#52c41a',
    color: 'white',
    '&.ant-tag': {
      backgroundColor: '#52c41a',
      color: 'white',
      border: 'none',
    }
  },
  incorrectTag: {
    backgroundColor: '#ff4d4f',
    color: 'white',
    '&.ant-tag': {
      backgroundColor: '#ff4d4f',
      color: 'white',
      border: 'none',
    }
  },
  carouselContainer: {
    padding: '1rem',
    maxWidth: '100%',
    display: 'flex',
    justifyContent: 'center',
    '& span': {
      color: `${theme.colors.primaryText} !important`,
    },
    '& span > svg': {
      width: '120%',
      height: '120%',
    },
    '& .ant-carousel .slick-slide > div': {
      display: 'flex !important',
      justifyContent: 'center',
      alignItems: 'center',
    }
  },
});

const QCDetailsTabModal = (props: any) => {
  const { classes, data, columns, reload } = props;

  const [imageModal, setImageModal] = React.useState({
    isVisible: false,
    images: [],
    title: '',
  });

  const handleOpenImageModal = (images: string[], title: string) => {
    setImageModal({
      isVisible: true,
      images: Array.isArray(images) ? images : [images],
      title,
    });
  };

  const handleCloseImageModal = () => {
    setImageModal({
      isVisible: false,
      images: [],
      title: '',
    });
  };

  const renderImageButton = (images: string[] | string, title: string) => {
    if (!images || (Array.isArray(images) && images.length === 0)) {
      return '-';
    }

    const imageArray = Array.isArray(images) ? images : [images];
    return (
      <span
        style={{
          color: '#1890ff',
          cursor: 'pointer',
          fontSize: '12px'
        }}
        onClick={() => handleOpenImageModal(imageArray, title)}
      >
        View ({imageArray.length})
      </span>
    );
  };

  const renderStatusIcon = (value: any, successText: string, failText: string) => {
    if (value !== true && value !== false) return '-';

    const isSuccess = value === true;
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isSuccess ? (
          <>
            <SuccessIcon style={{ marginRight: '4px' }} />
            {successText}
          </>
        ) : (
          <>
            <ErrorIcon2 style={{ marginRight: '4px' }} />
            {failText}
          </>
        )}
      </div>
    );
  };

  const generateQCColumns = () => {
    if (!columns || !Array.isArray(columns)) {
      return [];
    }

    // Adding serial number column first
    const tableColumns = [
      {
        title: 'Sr. No',
        dataIndex: 'sr_no',
        key: 'sr_no',
        width: 60,
        render: (text: any, record: any, index: number) => {
          return <span>{index + 1}</span>;
        },
      }
    ];

    // Generating columns from the columns prop
    columns.forEach((column: any) => {
      const columnConfig: any = {
        title: column.pretty_name || column.key,
        dataIndex: column.key,
        key: column.key,
        width: getColumnWidth(column.key),
        render: (value: any) => renderColumnValue(value, column.key, column.type),
      };

      tableColumns.push(columnConfig);
    });

    return tableColumns;
  };

  const getColumnWidth = (key: string) => {

    const widthMap: { [key: string]: number } = {
      'qc_question': 250,
      'qc_expected_answer': 120,
      'qc_reference_images': 130,
      'qc_rider_answer': 120,
      'qc_images_captured': 160,
      'qc_result': 100,
    };

    return widthMap[key] || 120; // Default width
  };

  const renderColumnValue = (value: any, columnKey: string, columnType: string) => {

    switch (columnKey) {
      case 'qc_question':
        return (
          <div style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {value || '-'}
          </div>
        );

      case 'qc_reference_images':
        return renderImageButton(value, 'Reference Images');

      case 'qc_images_captured':
        return renderImageButton(value, 'Images captured by the Rider');

      case 'qc_result':
        return renderStatusIcon(value, 'Correct', 'Incorrect');

      default:
        if (columnType === 'Array') {
          return renderImageButton(value, columnKey);
        }
        return value || '-';
    }
  };

  // Processing the QC data to match the expected format
  const processQCData = (qcData: any[]) => {

    if (!qcData || !Array.isArray(qcData)) {
      return [];
    }

    const processedData = qcData.map((item, index) => {

      return {
        key: index,
        sr_no: index + 1,
        ...item,
      };
    });

    return processedData;
  };

  const processedData = processQCData(data?.question_details);

  const renderDetailsCard = () => {
    return (
      <div className={classes.detailsCard}>
        <div className={classes.detailItem}>
          <div className={classes.detailLabel}>Packaging Material Number</div>
          <div className={classes.detailValue}>{data?.packaging_material_code || '-'}</div>
        </div>
        <div className={classes.detailItem}>
          <div className={classes.detailLabel}>QC Status</div>
          <div className={classes.detailValue}>
            {renderStatusIcon(data?.qc_status, 'Success', 'Failed')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      {renderDetailsCard()}
      <div className={classes.tableContainer}>
        <FixedHeaderTable
          locale={{ emptyText: 'No QC details available' }}
          pagination={false}
          dataSource={processedData}
          columns={generateQCColumns()}
          resizable={true}
        />

        {/* Image Modal */}
        {imageModal.isVisible && (
          <Modal
            title={imageModal.title}
            visible={imageModal.isVisible}
            onCancel={handleCloseImageModal}
            footer={null}
            centered
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh'
            }}
            bodyStyle={{
              padding: '16px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
          >
            <Carousel
              className={classes.carouselContainer}
              prevArrow={<LeftCircleOutlined />}
              nextArrow={<RightCircleOutlined />}
              arrows
            >
              {imageModal.images.map((imageUrl, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <img
                    src={imageUrl}
                    alt={`${imageModal.title} ${index + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                </div>
              ))}
            </Carousel>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles, { injectTheme: true })(QCDetailsTabModal);